import React from "react";
import { connect } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import mainHelper, { apiCall } from "../../helpers/MainHelper";
import ReactLoading from "react-loading";
import Swal from "sweetalert2";
import createVisibilityPoller from '../../services/VisibilityPoller';

class MessageDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      customer: null,
      messages: [],
      inputMessage: "",
      isLoad: true,
      sending: false,
      isUploadingFile: false,
      selectedFile: null,
      customerId: null,
      conversations: [],
      isLoadingConversations: false,
      searchQuery: "",
    };
    this.messagesEndRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.chatHistoryRef = React.createRef();
    this.messageRequests = new Set();
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const customerId = this.props.match.params.customerId;
    console.log('MessageDetail - customerId from route:', customerId);

    // Load conversations first (with loading indicator)
    this.loadConversations(true);

    if (customerId) {
      this.setState({ customerId });
      this.loadCustomerInfo(customerId);
      this.loadMessages(customerId).then(() => {
        // Scroll to bottom after initial messages are loaded
        setTimeout(() => {
          this.scrollToBottom();
        }, 800);
      });
    } else {
      // If no customerId, set loading to false
      this.setState({ isLoad: false });
    }

    // Auto-refresh messages and conversations every 10 seconds
    this.stopMessagePolling = createVisibilityPoller(async (signal) => {
      if (localStorage.getItem('isLoggedIn') !== 'true') return;
      const currentCustomerId = this.state.customerId;
      await Promise.all([
        currentCustomerId ? this.loadMessages(currentCustomerId, signal) : Promise.resolve(),
        this.loadConversations(false, signal),
      ]);
    }, 10000);
  }

  componentWillUnmount() {
    if (this.stopMessagePolling) this.stopMessagePolling();
  }

  componentDidUpdate(prevProps, prevState) {
    // Scroll to bottom when messages change
    if (prevState.messages.length !== this.state.messages.length) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 300);
    }

    // If route customerId changed, load new conversation
    if (prevProps.match.params.customerId !== this.props.match.params.customerId) {
      const customerId = this.props.match.params.customerId;
      if (customerId) {
        this.setState({ customerId, isLoad: true });
        this.loadCustomerInfo(customerId);
        this.loadMessages(customerId);
      } else {
        this.setState({ customer: null, messages: [], customerId: null, isLoad: false });
      }
    }
  }

  scrollToBottom = () => {
    // Try multiple methods for better compatibility
    if (this.messagesEndRef.current) {
      setTimeout(() => {
        this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }

    // Also try scrolling the chat history container
    if (this.chatHistoryRef.current) {
      setTimeout(() => {
        this.chatHistoryRef.current.scrollTop = this.chatHistoryRef.current.scrollHeight;
      }, 200);
    }
  };

  loadCustomerInfo = async (customerId) => {
    try {
      // Try customer endpoint first
      try {
        const response = await apiCall(`business/customer/info/${customerId}`, "get", null);
        if (response) {
          this.setState({
            customer: {
              ...response,
              is_user: false
            }
          });
          return;
        }
      } catch (customerError) {
        // If customer not found, try user endpoint
        console.log("Customer not found, trying user endpoint...");
      }

      // Try user endpoint
      try {
        const userResponse = await apiCall(`business/user/get-info-by-id/${customerId}`, "get", null);
        if (userResponse) {
          // Transform user data to customer format
          this.setState({
            customer: {
              id: userResponse.id,
              name: userResponse.name,
              surname: userResponse.surname || "",
              phone: userResponse.phone,
              email: userResponse.email,
              is_user: true
            }
          });
        }
      } catch (userError) {
        console.error("Error loading user info:", userError);
      }
    } catch (error) {
      console.error("Error loading customer info:", error);
    }
  };

  loadMessages = async (customerId, signal) => {
    const key = String(customerId);
    if (this.messageRequests.has(key)) return;
    this.messageRequests.add(key);
    try {
      const response = await apiCall(`business/messages/${customerId}`, "get", null, { background: true, signal });
      if ((signal && signal.aborted) || String(this.state.customerId) !== key) return;

      return new Promise((resolve) => {
        if (response && response.status === 'success') {
          // Set messages
          if (Array.isArray(response.data)) {
            this.setState({ messages: response.data, isLoad: false }, () => {
              // Scroll to bottom after messages are set
              setTimeout(() => {
                this.scrollToBottom();
              }, 300);
              resolve();
            });
          } else {
            this.setState({ messages: [], isLoad: false }, () => resolve());
          }

          // Set customer info from response if available
          if (response.customer) {
            this.setState({ customer: response.customer });
          }
        } else if (response && Array.isArray(response)) {
          // Fallback: if response is directly an array
          this.setState({ messages: response, isLoad: false }, () => {
            // Scroll to bottom after messages are set
            setTimeout(() => {
              this.scrollToBottom();
            }, 300);
            resolve();
          });
        } else {
          this.setState({ messages: [], isLoad: false }, () => resolve());
        }
      });
    } catch (error) {
      if (error.code === 'ERR_CANCELED' || String(this.state.customerId) !== key) return;
      console.error("Error loading messages:", error);
      this.setState({ isLoad: false });
      return Promise.resolve();
    } finally {
      this.messageRequests.delete(key);
    }
  };

  handleSend = async () => {
    const { inputMessage, customerId, selectedFile } = this.state;

    console.log('handleSend - customerId:', customerId, 'message:', inputMessage);

    // If file is selected, send file instead
    if (selectedFile) {
      this.handleSendFile();
      return;
    }

    if (!inputMessage.trim() || !customerId) {
      console.warn('handleSend - Missing inputMessage or customerId');
      return;
    }

    this.setState({ sending: true });

    try {
      const requestData = {
        customer_id: parseInt(customerId), // Ensure it's an integer
        message: inputMessage.trim(),
      };

      console.log('handleSend - Sending request:', requestData);

      const response = await apiCall("business/messages/send", "post", requestData);

      console.log('handleSend - Response:', response);

      if (response && response.status === 'success') {
        // Reload messages
        await this.loadMessages(customerId);
        this.setState({ inputMessage: "" });
      } else {
        throw new Error(response?.message || 'Mesaj gönderilemedi');
      }
    } catch (error) {
      console.error("Error sending message:", error);
      console.error("Error response:", error.response?.data);

      const errorMessage = error.response?.data?.message || error.message || "Mesaj gönderilemedi. Lütfen tekrar deneyin.";

      Swal.fire({
        title: "Hata",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "Tamam",
      });
    } finally {
      this.setState({ sending: false });
    }
  };

  handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          title: "Hata",
          text: "Dosya boyutu 10MB'dan büyük olamaz.",
          icon: "error",
          confirmButtonText: "Tamam",
        });
        return;
      }

      this.setState({
        selectedFile: {
          file: file,
          name: file.name,
          size: file.size,
          type: file.type,
        }
      });
    }
    // Reset input value to allow selecting the same file again
    e.target.value = null;
  };

  handleRemoveFile = () => {
    this.setState({ selectedFile: null });
  };

  handleSendFile = async () => {
    const { selectedFile, customerId, inputMessage } = this.state;

    if (!selectedFile || !customerId) {
      return;
    }

    this.setState({ isUploadingFile: true });

    try {
      const formData = new FormData();
      formData.append('customer_id', parseInt(customerId));
      formData.append('file', selectedFile.file);

      // Add optional message text if input has text
      if (inputMessage.trim()) {
        formData.append('message', inputMessage.trim());
      }

      const response = await apiCall("business/messages/send-file", "post", formData);

      if (response && response.status === 'success') {
        // Reload messages
        await this.loadMessages(customerId);
        this.setState({ inputMessage: "", selectedFile: null });
      } else {
        throw new Error(response?.message || 'Dosya gönderilemedi');
      }
    } catch (error) {
      console.error("Error sending file:", error);
      console.error("Error response:", error.response?.data);

      const errorMessage = error.response?.data?.message || error.message || "Dosya gönderilemedi. Lütfen tekrar deneyin.";

      Swal.fire({
        title: "Hata",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "Tamam",
      });
    } finally {
      this.setState({ isUploadingFile: false });
    }
  };

  formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  handleFileClick = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  loadConversations = async (showLoading = false, signal) => {
    // Don't reload if already loading (unless explicitly requested)
    if (this.conversationsInFlight) {
      return;
    }
    this.conversationsInFlight = true;
    try {
      if (showLoading) {
        this.setState({ isLoadingConversations: true });
      }

      const response = await apiCall("business/messages/conversations", "get", null, { background: true, signal });
      if (signal && signal.aborted) return;

      if (response && response.data && Array.isArray(response.data)) {
        this.setState({ conversations: response.data, isLoadingConversations: false });
      } else if (response && Array.isArray(response)) {
        this.setState({ conversations: response, isLoadingConversations: false });
      } else {
        this.setState({ conversations: [], isLoadingConversations: false });
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;
      console.error("Error loading conversations:", error);
      this.setState({ isLoadingConversations: false });
    } finally {
      this.conversationsInFlight = false;
    }
  };

  handleConversationClick = (conversationId) => {
    this.props.history.push(`/messages/${conversationId}`);
  };

  formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (days === 1) {
      return "Dün";
    } else if (days < 7) {
      return date.toLocaleDateString("tr-TR", { weekday: "short" });
    } else {
      return date.toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "short",
      });
    }
  };

  formatConversationTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  render() {
    const { customer, messages, inputMessage, isLoad, sending, conversations, isLoadingConversations, searchQuery, customerId } = this.state;

    // Filter conversations based on search query
    const filteredConversations = conversations.filter(conv => {
      if (!searchQuery) return true;
      const searchLower = searchQuery.toLowerCase();
      const customerName = conv.customer?.name || "";
      const customerPhone = conv.customer?.phone || "";
      const lastMessage = conv.last_message?.message || "";
      return (
        customerName.toLowerCase().includes(searchLower) ||
        customerPhone.includes(searchLower) ||
        lastMessage.toLowerCase().includes(searchLower)
      );
    });

    return (
      <div style={{ flex: 1 }}>
        <div>
          <div className="page-loader-wrapper" style={{ display: isLoad ? "block" : "none" }}>
            <div className="loader" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div className="m-t-30">
                <img src={require("../../assets/images/logo-white.png")} width="150" alt="Lucid" />
              </div>
              <p>Yükleniyor</p>
              <ReactLoading type="spin" width={"24px"} height={"24px"} color="#ffffff" />
            </div>
          </div>
          <div className="container-fluid">
            <PageHeader
              HeaderText={customer ? `${customer.name} ${customer.surname || ""}` : "Mesajlaşma"}
              Breadcrumb={[
                { name: "Mesajlar", navigate: "messages" },
                { name: customer ? `${customer.name} ${customer.surname || ""}` : "Detay" },
              ]}
            />

            <div className="row">
              <div className="col-lg-12">
                <div className="card chat-app">
                  <div id="plist" className="people-list">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">
                          <i className="fa fa-search"></i>
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Konuşma Ara..."
                        value={searchQuery}
                        onChange={(e) => this.setState({ searchQuery: e.target.value })}
                      />
                    </div>
                    <ul className="list-unstyled chat-list mt-2 mb-0">
                      {isLoadingConversations ? (
                        <li className="text-center p-3" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "200px" }}>
                          <ReactLoading type="spin" width={"24px"} height={"24px"} color="#007bff" />
                          <p className="mt-3 mb-0 text-muted" style={{ fontSize: "14px" }}>Konuşmalar yükleniyor...</p>
                        </li>
                      ) : filteredConversations.length === 0 ? (
                        <li className="text-center p-3 text-muted">
                          {searchQuery ? "Sonuç bulunamadı" : "Henüz konuşma yok"}
                        </li>
                      ) : (
                        filteredConversations.map((conv) => {
                          const isActive = conv.id === parseInt(customerId);
                          const customerName = conv.customer?.name || "Müşteri";
                          const customerPhone = conv.customer?.phone || "";
                          const lastMessage = conv.last_message?.message || "";
                          const unreadCount = conv.unread_count || 0;

                          return (
                            <li
                              key={conv.id}
                              className={isActive ? "active clearfix" : "clearfix"}
                              onClick={() => this.handleConversationClick(conv.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <img
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(customerName)}&size=45&background=007bff&color=fff`}
                                alt="avatar"
                              />
                              <div className="about">
                                <div className="name">{customerName}</div>
                                <div className="status">
                                  {lastMessage.length > 30
                                    ? lastMessage.substring(0, 30) + "..."
                                    : lastMessage || "Mesaj yok"}
                                </div>
                              </div>
                              <div style={{ float: "right", textAlign: "right" }}>
                                {unreadCount > 0 && (
                                  <span className="badge badge-danger" style={{ marginBottom: "5px" }}>
                                    {unreadCount}
                                  </span>
                                )}
                                <div className="status" style={{ fontSize: "11px", color: "#999" }}>
                                  {this.formatConversationTime(conv.last_message?.created_at || conv.updated_at)}
                                </div>
                              </div>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  </div>
                  <div className="chat" style={{ marginLeft: "280px" }}>
                    <div className="chat-header clearfix">
                      <div className="row">
                        <div className="col-lg-6">
                          {customer ? (
                            <>
                              <img
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent((customer.name || "") + " " + (customer.surname || ""))}&size=40&background=007bff&color=fff`}
                                alt="avatar"
                                className="rounded-circle"
                                style={{ width: "40px", height: "40px", marginRight: "10px" }}
                              />
                              <div className="chat-about">
                                <h6 className="m-b-0">
                                  {customer.name} {customer.surname || ""}
                                </h6>
                                {customer.phone && (
                                  <small>{customer.phone}</small>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="chat-about">
                              <h6 className="m-b-0">Müşteri Bilgisi Yükleniyor...</h6>
                            </div>
                          )}
                        </div>
                        <div className="col-lg-6 hidden-sm text-right">
                          {customer && (
                            <>
                              {customer.phone && (
                                <a
                                  href={`tel:${customer.phone}`}
                                  className="btn btn-sm btn-outline-success"
                                  style={{ marginRight: "5px" }}
                                >
                                  <i className="icon-phone"></i> Ara
                                </a>
                              )}
                              {customer.id && !customer.is_user && (
                                <Link
                                  to={`/customer-detail/${customer.id}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="icon-user"></i> Müşteri Detayı
                                </Link>
                              )}
                              {customer.is_user && (
                                <span className="badge badge-info" style={{ padding: "8px 12px" }}>
                                  <i className="icon-user"></i> Mobil Kullanıcı
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      ref={this.chatHistoryRef}
                      className="chat-history"
                      style={{ height: "500px", overflowY: "auto" }}
                    >
                      {!customerId ? (
                        <div style={{ textAlign: "center", padding: "60px 20px" }}>
                          <i className="icon-bubble" style={{ fontSize: "64px", color: "#ccc", marginBottom: "20px" }}></i>
                          <h5>Bir konuşma seçin</h5>
                          <p className="text-muted">Sol taraftan bir konuşma seçerek mesajlaşmaya başlayın.</p>
                        </div>
                      ) : messages.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "60px 20px" }}>
                          <i className="icon-bubble" style={{ fontSize: "64px", color: "#ccc", marginBottom: "20px" }}></i>
                          <h5>Henüz mesaj yok</h5>
                          <p className="text-muted">İlk mesajınızı göndererek başlayın.</p>
                        </div>
                      ) : (
                        <ul className="m-b-0">
                          {messages.map((msg) => {
                            const isService = msg.sender_type === "service";
                            const senderName = isService
                              ? "Servis"
                              : (customer ? `${customer.name} ${customer.surname || ""}` : "Müşteri");

                            return (
                              <li
                                key={msg.id}
                                className={isService ? "clearfix" : "clearfix odd"}
                              >
                                {isService ? (
                                  <>
                                    <div className="message-data text-right">
                                      <span className="message-data-time">
                                        {this.formatTime(msg.created_at)}
                                      </span>
                                    </div>
                                    <div className="message my-message float-right">
                                      {msg.file_url ? (
                                        <div>
                                          {msg.file_type && msg.file_type.startsWith('image/') ? (
                                            <div style={{ marginBottom: msg.message ? '8px' : '0' }}>
                                              <img
                                                src={msg.file_url}
                                                alt={msg.file_name || 'Dosya'}
                                                style={{
                                                  maxWidth: '300px',
                                                  maxHeight: '300px',
                                                  borderRadius: '8px',
                                                  cursor: 'pointer'
                                                }}
                                                onClick={() => this.handleFileClick(msg.file_url)}
                                              />
                                            </div>
                                          ) : (
                                            <div
                                              style={{
                                                padding: '12px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                cursor: 'pointer',
                                                display: 'inline-block',
                                                marginBottom: msg.message ? '8px' : '0'
                                              }}
                                              onClick={() => this.handleFileClick(msg.file_url)}
                                            >
                                              <i className="fa fa-file" style={{ marginRight: '8px', fontSize: '18px' }}></i>
                                              <span style={{ fontWeight: 'bold' }}>{msg.file_name || 'Dosya'}</span>
                                              {msg.file_size && (
                                                <span style={{ marginLeft: '8px', color: '#6c757d', fontSize: '12px' }}>
                                                  ({this.formatFileSize(msg.file_size)})
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          {msg.message && (
                                            <div style={{ marginTop: '8px' }}>{msg.message}</div>
                                          )}
                                        </div>
                                      ) : (
                                        msg.message
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="message-data">
                                      <img
                                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(senderName)}&size=40&background=e8f1f3&color=444`}
                                        alt="avatar"
                                      />
                                      <span className="message-data-time">
                                        {this.formatTime(msg.created_at)}
                                      </span>
                                    </div>
                                    <div className="message other-message">
                                      {msg.file_url ? (
                                        <div>
                                          {msg.file_type && msg.file_type.startsWith('image/') ? (
                                            <div style={{ marginBottom: msg.message ? '8px' : '0' }}>
                                              <img
                                                src={msg.file_url}
                                                alt={msg.file_name || 'Dosya'}
                                                style={{
                                                  maxWidth: '300px',
                                                  maxHeight: '300px',
                                                  borderRadius: '8px',
                                                  cursor: 'pointer'
                                                }}
                                                onClick={() => this.handleFileClick(msg.file_url)}
                                              />
                                            </div>
                                          ) : (
                                            <div
                                              style={{
                                                padding: '12px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '8px',
                                                border: '1px solid #dee2e6',
                                                cursor: 'pointer',
                                                display: 'inline-block',
                                                marginBottom: msg.message ? '8px' : '0'
                                              }}
                                              onClick={() => this.handleFileClick(msg.file_url)}
                                            >
                                              <i className="fa fa-file" style={{ marginRight: '8px', fontSize: '18px' }}></i>
                                              <span style={{ fontWeight: 'bold' }}>{msg.file_name || 'Dosya'}</span>
                                              {msg.file_size && (
                                                <span style={{ marginLeft: '8px', color: '#6c757d', fontSize: '12px' }}>
                                                  ({this.formatFileSize(msg.file_size)})
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          {msg.message && (
                                            <div style={{textAlign:'left', marginTop: '8px' }}>{msg.message}</div>
                                          )}
                                        </div>
                                      ) : (
                                        msg.message
                                      )}
                                    </div>
                                  </>
                                )}
                              </li>
                            );
                          })}
                          <div ref={this.messagesEndRef} />
                        </ul>
                      )}
                    </div>
                    {customerId && (
                      <>
                        {this.state.selectedFile && (
                          <div className="chat-message clearfix" style={{ padding: '10px 15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <i className="fa fa-file" style={{ fontSize: '20px', marginRight: '10px', color: '#007bff' }}></i>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 'bold', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {this.state.selectedFile.name}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                    {this.formatFileSize(this.state.selectedFile.size)}
                                  </div>
                                </div>
                              </div>
                              <button
                                className="btn btn-sm btn-link"
                                onClick={this.handleRemoveFile}
                                style={{ color: '#dc3545', padding: '0 5px' }}
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="chat-message clearfix">
                          <div className="input-group mb-0">
                            <div className="input-group-prepend">
                              <button
                                className="input-group-text"
                                onClick={() => this.fileInputRef.current?.click()}
                                style={{ cursor: 'pointer' }}
                                disabled={sending || this.state.isUploadingFile}
                              >
                                <i className="fa fa-paperclip"></i>
                              </button>
                            </div>
                            <input
                              type="file"
                              ref={this.fileInputRef}
                              style={{ display: 'none' }}
                              onChange={this.handleFileSelect}
                              accept="*/*"
                            />
                            <input
                              type="text"
                              className="form-control"
                              placeholder={this.state.selectedFile ? "Mesajınızı yazın (opsiyonel)..." : "Mesajınızı buraya yazın..."}
                              value={inputMessage}
                              onChange={(e) => this.setState({ inputMessage: e.target.value })}
                              onKeyPress={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  this.handleSend();
                                }
                              }}
                              disabled={sending || this.state.isUploadingFile}
                            />
                            <div className="input-group-prepend">
                              <button
                                className="input-group-text"
                                onClick={this.handleSend}
                                disabled={(!inputMessage.trim() && !this.state.selectedFile) || sending || this.state.isUploadingFile}
                              >
                                {(sending || this.state.isUploadingFile) ? (
                                  <ReactLoading type="spin" width={"18px"} height={"18px"} color="#000" />
                                ) : (
                                  <i className="fa fa-send"></i>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(connect()(MessageDetail));
